import { useState, useEffect, useCallback } from 'react';
import { Check, AlertCircle, Database } from 'lucide-react';
import api from '../services/api';

interface SystemHealthProps {
  className?: string;
  showDetails?: boolean;
}



interface SystemHealth {
  status: string;
  database_connections: {
    mysql: {
      healthy: boolean;
      message: string;
    };
    colefax_po: {
      healthy: boolean;
      message: string;
    };
    colefax_atera: {
      healthy: boolean;
      message: string;
    };
  };
  lastSync?: {
    timestamp: string;
    success: boolean;
  }
}

/**
 * SystemHealthIndicator Component
 * Displays real-time system health status including database connections
 * Shows graceful degradation when external databases are unavailable
 */
const SystemHealthIndicator: React.FC<SystemHealthProps> = ({ className = '', showDetails = false }) => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get system health status from localStorage or return null
  const getLastSyncFromStorage = (): { timestamp: string; success: boolean } | null => {
    const stored = localStorage.getItem('last_sync_info');
    return stored ? JSON.parse(stored) : null;
  };

  const fetchSystemHealth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/health');
      
      // Get last sync info from localStorage
      const lastSyncInfo = getLastSyncFromStorage();
      
      setHealth({
        ...response.data,
        lastSync: lastSyncInfo || undefined
      });
      
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to fetch system health:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showDetails) {
      fetchSystemHealth();
    }
  }, [showDetails, fetchSystemHealth]);

  if (!showDetails) {
    return null;
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-sm ${className}`}>
        <div className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <span className="text-gray-500">Checking system health...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 text-sm text-red-500 ${className}`}>
        <AlertCircle className="w-4 h-4" />
        <span>Health check failed</span>
      </div>
    );
  }

  if (!health || !health.database_connections) {
    return (
      <div className={`p-4 border rounded-lg ${className}`}>
        <h3 className="text-sm font-medium mb-2 dark:text-white">System Health</h3>
        <div className="flex items-center gap-2 text-sm text-yellow-500">
          <AlertCircle className="w-4 h-4" />
          <span>Health data unavailable</span>
        </div>
      </div>
    );
  }

  const remoteDbStatus = health.database_connections?.colefax_po?.healthy && 
                        health.database_connections?.colefax_atera?.healthy;

  const getStatusColor = (healthy: boolean) => {
    return healthy ? 'text-green-500' : 'text-red-500';
  };

  const getLastSyncText = () => {
    if (!health.lastSync) return 'Never synced';
    
    const date = new Date(health.lastSync.timestamp);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString();
    
    return `Last sync: ${formattedDate} ${formattedTime}`;
  };

  return (
    <div className={`p-4 border rounded-lg ${className}`}>
      <h3 className="text-sm font-medium mb-2 dark:text-white">System Health</h3>
      
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 dark:text-white" />
          <span className="text-xs dark:text-white">Local DB:</span>
          <span className={`text-xs ${getStatusColor(health.database_connections?.mysql?.healthy || false)}`}>
            {health.database_connections?.mysql?.healthy ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 dark:text-white" />
          <span className="text-xs dark:text-white">Remote DBs:</span>
          <span className={`text-xs ${remoteDbStatus ? 'text-green-500' : 'text-yellow-500'}`}>
            {remoteDbStatus ? 'Connected' : 'Fallback mode'}
          </span>
        </div>
        
        {health.lastSync && (
          <div className="flex items-center gap-2">
            {health.lastSync.success ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-yellow-500" />
            )}
            <span className="text-xs dark:text-white">{getLastSyncText()}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemHealthIndicator;
// Also export the update function to be used by Dashboard
export const updateSyncStatus = (success: boolean) => {
  const timestamp = new Date().toISOString();
  localStorage.setItem('last_sync_info', JSON.stringify({ timestamp, success }));
}; 